import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { Layout } from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Calendar, MapPin, Clock, Plus, Edit2, Trash2, Ticket, Upload, Image, X, Share2, Link2, Check } from 'lucide-react';
import { toast } from 'sonner';
import { SITE_URL } from '@/config/assets';

const API_URL = process.env.REACT_APP_BACKEND_URL;
// Custom domain for sharing - this will be shown in share links
const SHARE_DOMAIN = SITE_URL;

const initialFormState = {
  name: '',
  date: '',
  time: '',
  venue: '',
  description: '',
  ticket_price: '',
  total_tickets: '',
  event_type: 'football',
  categories: []
};

const defaultCategories = [
  { name: 'General', price: 50, quantity: 100, description: 'Standard seating' },
  { name: 'VIP', price: 150, quantity: 50, description: 'Premium seating with better view' },
  { name: 'VVIP', price: 300, quantity: 20, description: 'Best seats with exclusive access' }
];

export default function EventsPage() {
  const { getAuthHeader } = useAuth();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [flyerDialogOpen, setFlyerDialogOpen] = useState(false);
  const [selectedEventForFlyer, setSelectedEventForFlyer] = useState(null);
  const [editingEvent, setEditingEvent] = useState(null);
  const [formData, setFormData] = useState(initialFormState);
  const [submitting, setSubmitting] = useState(false);
  const [uploadingFlyer, setUploadingFlyer] = useState(false);
  const [useCategories, setUseCategories] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchEvents();
  }, []);

  const fetchEvents = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/events`, getAuthHeader());
      setEvents(response.data);
    } catch (error) {
      toast.error('Failed to fetch events');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleCategoryChange = (index, field, value) => {
    const newCategories = [...formData.categories];
    newCategories[index] = { ...newCategories[index], [field]: value };
    setFormData(prev => ({ ...prev, categories: newCategories }));
  };

  const addCategory = () => {
    setFormData(prev => ({
      ...prev,
      categories: [...prev.categories, { name: '', price: 0, quantity: 0, description: '' }]
    }));
  };

  const removeCategory = (index) => {
    setFormData(prev => ({
      ...prev,
      categories: prev.categories.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    // Build categories array with proper types
    const categoriesPayload = useCategories && formData.categories.length > 0
      ? formData.categories.map(cat => ({
          name: cat.name,
          price: parseFloat(cat.price) || 0,
          quantity: parseInt(cat.quantity) || 0,
          description: cat.description || ''
        }))
      : null;

    // Calculate total tickets from categories if using categories
    let totalTickets = parseInt(formData.total_tickets);
    if (categoriesPayload) {
      totalTickets = categoriesPayload.reduce((sum, cat) => sum + cat.quantity, 0);
    }

    const payload = {
      ...formData,
      ticket_price: parseFloat(formData.ticket_price),
      total_tickets: totalTickets,
      categories: categoriesPayload
    };

    try {
      if (editingEvent) {
        await axios.put(`${API_URL}/api/events/${editingEvent.id}`, payload, getAuthHeader());
        toast.success('Event updated successfully');
      } else {
        await axios.post(`${API_URL}/api/events`, payload, getAuthHeader());
        toast.success('Event created successfully');
      }
      fetchEvents();
      setDialogOpen(false);
      setFormData(initialFormState);
      setEditingEvent(null);
      setUseCategories(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save event');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (event) => {
    setEditingEvent(event);
    const hasCategories = event.categories && event.categories.length > 0;
    setUseCategories(hasCategories);
    setFormData({
      name: event.name,
      date: event.date,
      time: event.time,
      venue: event.venue,
      description: event.description,
      ticket_price: event.ticket_price.toString(),
      total_tickets: event.total_tickets.toString(),
      event_type: event.event_type,
      categories: hasCategories ? event.categories : []
    });
    setDialogOpen(true);
  };

  const handleOpenNewDialog = () => {
    setEditingEvent(null);
    setFormData(initialFormState);
    setUseCategories(false);
    setDialogOpen(true);
  };

  const handleDelete = async (eventId) => {
    if (!window.confirm('Are you sure you want to delete this event? All associated tickets will also be deleted.')) {
      return;
    }

    try {
      await axios.delete(`${API_URL}/api/events/${eventId}`, getAuthHeader());
      toast.success('Event deleted successfully');
      fetchEvents();
    } catch (error) {
      toast.error('Failed to delete event');
    }
  };

  const openCreateDialog = () => {
    setEditingEvent(null);
    setFormData(initialFormState);
    setUseCategories(false);
    setDialogOpen(true);
  };

  const handleOpenFlyerDialog = (event) => {
    setSelectedEventForFlyer(event);
    setFlyerDialogOpen(true);
  };

  const handleFlyerUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      toast.error('Please upload a valid image (JPG, PNG, GIF, WEBP)');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error('File size must be less than 5MB');
      return;
    }

    setUploadingFlyer(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      await axios.post(
        `${API_URL}/api/events/${selectedEventForFlyer.id}/flyer`,
        formData,
        {
          ...getAuthHeader(),
          headers: {
            ...getAuthHeader().headers,
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      toast.success('Flyer uploaded successfully');
      fetchEvents();
      setFlyerDialogOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload flyer');
    } finally {
      setUploadingFlyer(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  return (
    <Layout title="Events">
      <div className="flex justify-between items-center mb-6">
        <p className="text-muted-foreground">Manage football matches and events</p>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button 
              onClick={openCreateDialog}
              className="bg-[#0055FF] hover:bg-[#0044CC]"
              data-testid="create-event-button"
            >
              <Plus className="w-4 h-4 mr-2" />
              Create Event
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl font-bold">
                {editingEvent ? 'Edit Event' : 'Create New Event'}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label htmlFor="name" className="text-xs uppercase tracking-wide font-medium">Event Name</Label>
                <Input
                  id="name"
                  name="name"
                  data-testid="event-name-input"
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder="e.g., Hawks vs Lions"
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="date" className="text-xs uppercase tracking-wide font-medium">Date</Label>
                  <Input
                    id="date"
                    name="date"
                    type="date"
                    data-testid="event-date-input"
                    value={formData.date}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="time" className="text-xs uppercase tracking-wide font-medium">Time</Label>
                  <Input
                    id="time"
                    name="time"
                    type="time"
                    data-testid="event-time-input"
                    value={formData.time}
                    onChange={handleInputChange}
                    required
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="venue" className="text-xs uppercase tracking-wide font-medium">Venue</Label>
                <Input
                  id="venue"
                  name="venue"
                  data-testid="event-venue-input"
                  value={formData.venue}
                  onChange={handleInputChange}
                  placeholder="e.g., Independence Stadium, Bakau"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="event_type" className="text-xs uppercase tracking-wide font-medium">Event Type</Label>
                <Select
                  value={formData.event_type}
                  onValueChange={(value) => setFormData(prev => ({ ...prev, event_type: value }))}
                >
                  <SelectTrigger data-testid="event-type-select">
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="football">Football Match</SelectItem>
                    <SelectItem value="concert">Concert</SelectItem>
                    <SelectItem value="other">Other Event</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="ticket_price" className="text-xs uppercase tracking-wide font-medium">
                    {useCategories ? 'Base Price (D)' : 'Ticket Price (D)'}
                  </Label>
                  <Input
                    id="ticket_price"
                    name="ticket_price"
                    type="number"
                    step="0.01"
                    min="0"
                    data-testid="event-price-input"
                    value={formData.ticket_price}
                    onChange={handleInputChange}
                    placeholder="100"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="total_tickets" className="text-xs uppercase tracking-wide font-medium">
                    {useCategories ? 'Total (Auto)' : 'Total Tickets'}
                  </Label>
                  <Input
                    id="total_tickets"
                    name="total_tickets"
                    type="number"
                    min="1"
                    data-testid="event-tickets-input"
                    value={useCategories 
                      ? formData.categories.reduce((sum, cat) => sum + (parseInt(cat.quantity) || 0), 0)
                      : formData.total_tickets
                    }
                    onChange={handleInputChange}
                    placeholder="500"
                    required={!useCategories}
                    disabled={useCategories}
                  />
                </div>
              </div>

              {/* Ticket Categories Section */}
              <div className="space-y-3 border-t border-border pt-4">
                <div className="flex items-center justify-between">
                  <Label className="text-xs uppercase tracking-wide font-medium">Ticket Categories</Label>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      if (!useCategories) {
                        setFormData(prev => ({ ...prev, categories: defaultCategories }));
                      }
                      setUseCategories(!useCategories);
                    }}
                  >
                    {useCategories ? 'Use Single Price' : 'Add Categories'}
                  </Button>
                </div>

                {useCategories && (
                  <div className="space-y-3">
                    {formData.categories.map((cat, index) => (
                      <div key={cat.name || `category-${index}`} className="p-3 border rounded-lg bg-gray-50 space-y-2">
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium">Category {index + 1}</span>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => removeCategory(index)}
                            className="h-6 w-6 p-0 text-red-500 hover:text-red-700"
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                        <div className="grid grid-cols-3 gap-2">
                          <Input
                            placeholder="Name (e.g., VIP)"
                            value={cat.name}
                            onChange={(e) => handleCategoryChange(index, 'name', e.target.value)}
                            className="text-sm"
                          />
                          <Input
                            type="number"
                            placeholder="Price"
                            value={cat.price}
                            onChange={(e) => handleCategoryChange(index, 'price', e.target.value)}
                            className="text-sm"
                          />
                          <Input
                            type="number"
                            placeholder="Quantity"
                            value={cat.quantity}
                            onChange={(e) => handleCategoryChange(index, 'quantity', e.target.value)}
                            className="text-sm"
                          />
                        </div>
                        <Input
                          placeholder="Description (optional)"
                          value={cat.description}
                          onChange={(e) => handleCategoryChange(index, 'description', e.target.value)}
                          className="text-sm"
                        />
                      </div>
                    ))}
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={addCategory}
                      className="w-full"
                    >
                      <Plus className="w-4 h-4 mr-1" /> Add Category
                    </Button>
                  </div>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="description" className="text-xs uppercase tracking-wide font-medium">Description (Optional)</Label>
                <Textarea
                  id="description"
                  name="description"
                  data-testid="event-description-input"
                  value={formData.description}
                  onChange={handleInputChange}
                  placeholder="Event description..."
                  rows={3}
                />
              </div>

              <div className="flex gap-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setDialogOpen(false)}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={submitting}
                  className="flex-1 bg-[#0055FF] hover:bg-[#0044CC]"
                  data-testid="event-submit-button"
                >
                  {submitting ? 'Saving...' : (editingEvent ? 'Update Event' : 'Create Event')}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[...Array(3)].map((_, i) => (
            <div key={`skeleton-${i}`} className="h-48 bg-white rounded border border-border animate-pulse" />
          ))}
        </div>
      ) : events.length === 0 ? (
        <Card className="border border-border bg-white">
          <CardContent className="py-12 text-center">
            <Calendar className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
            <p className="text-muted-foreground">No events yet. Create your first event!</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {events.map((event) => (
            <Card key={event.id} className="border border-border bg-white overflow-hidden" data-testid={`event-card-${event.id}`}>
              {/* Event Flyer */}
              {event.flyer_url ? (
                <div className="h-40 bg-muted overflow-hidden relative group">
                  <img 
                    src={`${API_URL}${event.flyer_url}`}
                    alt={event.name}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => handleOpenFlyerDialog(event)}
                    >
                      <Upload className="w-4 h-4 mr-2" />
                      Change Flyer
                    </Button>
                  </div>
                </div>
              ) : (
                <div 
                  className="h-32 bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center cursor-pointer hover:from-gray-200 hover:to-gray-300 transition-colors"
                  onClick={() => handleOpenFlyerDialog(event)}
                >
                  <div className="text-center">
                    <Image className="w-8 h-8 mx-auto text-muted-foreground mb-2" />
                    <span className="text-xs text-muted-foreground">Click to add flyer</span>
                  </div>
                </div>
              )}

              <CardHeader className="pb-2">
                <div className="flex justify-between items-start">
                  <Badge variant="outline" className="text-xs uppercase tracking-wide">
                    {event.event_type}
                  </Badge>
                  <div className="flex gap-1">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => {
                        // Use custom domain for share links
                        const shareUrl = `${SHARE_DOMAIN}/api/events/${event.id}/share`;
                        navigator.clipboard.writeText(shareUrl);
                        toast.success('Event share link copied! This link will show the event image when shared.');
                      }}
                      className="h-8 w-8 text-blue-600 hover:text-blue-800"
                      data-testid={`share-event-${event.id}`}
                      title="Copy shareable link (with image preview)"
                    >
                      <Link2 className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={async () => {
                        // Use custom domain for share links
                        const shareUrl = `${SHARE_DOMAIN}/api/events/${event.id}/share`;
                        if (navigator.share) {
                          try {
                            await navigator.share({
                              title: event.name,
                              text: `Check out this event: ${event.name} on ${event.date} at ${event.venue}`,
                              url: shareUrl
                            });
                          } catch (err) {
                            if (err.name !== 'AbortError') {
                              navigator.clipboard.writeText(shareUrl);
                              toast.success('Event share link copied!');
                            }
                          }
                        } else {
                          navigator.clipboard.writeText(shareUrl);
                          toast.success('Event share link copied!');
                        }
                      }}
                      className="h-8 w-8 text-green-600 hover:text-green-800"
                      data-testid={`share-native-event-${event.id}`}
                      title="Share event"
                    >
                      <Share2 className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleEdit(event)}
                      className="h-8 w-8"
                      data-testid={`edit-event-${event.id}`}
                    >
                      <Edit2 className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleDelete(event.id)}
                      className="h-8 w-8 text-destructive hover:text-destructive"
                      data-testid={`delete-event-${event.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
                <CardTitle className="font-heading text-xl font-bold tracking-tight mt-2">
                  {event.name}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2 text-sm">
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Calendar className="w-4 h-4" />
                    <span>{event.date}</span>
                  </div>
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Clock className="w-4 h-4" />
                    <span>{event.time}</span>
                  </div>
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <MapPin className="w-4 h-4" />
                    <span>{event.venue}</span>
                  </div>
                </div>

                <div className="mt-4 pt-4 border-t border-border">
                  <div className="flex justify-between items-center">
                    <div>
                      <p className="text-xs text-muted-foreground uppercase tracking-wide">
                        {event.categories?.length > 0 ? 'From' : 'Price'}
                      </p>
                      <p className="font-heading text-lg font-bold">
                        D{event.categories?.length > 0 
                          ? Math.min(...event.categories.map(c => c.price))
                          : event.ticket_price
                        }
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-xs text-muted-foreground uppercase tracking-wide">Tickets</p>
                      <p className="font-heading text-lg font-bold">
                        <span className="text-[#00C853]">{event.tickets_sold}</span>
                        <span className="text-muted-foreground">/{event.total_tickets}</span>
                      </p>
                    </div>
                  </div>
                  {event.categories?.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1">
                      {event.categories.map((cat) => (
                        <Badge key={cat.name} variant="secondary" className="text-xs">
                          {cat.name}: D{cat.price}
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Flyer Upload Dialog */}
      <Dialog open={flyerDialogOpen} onOpenChange={setFlyerDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl font-bold">
              Upload Event Flyer
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <p className="text-sm text-muted-foreground">
              Upload a flyer image for <strong>{selectedEventForFlyer?.name}</strong>
            </p>
            
            {selectedEventForFlyer?.flyer_url && (
              <div className="mb-4">
                <p className="text-xs uppercase tracking-wide text-muted-foreground mb-2">Current Flyer</p>
                <img 
                  src={`${API_URL}${selectedEventForFlyer.flyer_url}`}
                  alt="Current flyer"
                  className="w-full h-40 object-cover rounded-lg border"
                />
              </div>
            )}

            <div className="border-2 border-dashed border-border rounded-lg p-8 text-center">
              <Upload className="w-10 h-10 mx-auto text-muted-foreground mb-3" />
              <p className="text-sm text-muted-foreground mb-3">
                Drag and drop or click to upload
              </p>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/gif,image/webp"
                onChange={handleFlyerUpload}
                className="hidden"
                id="flyer-upload"
                data-testid="flyer-upload-input"
              />
              <Button
                type="button"
                variant="outline"
                disabled={uploadingFlyer}
                onClick={() => fileInputRef.current?.click()}
              >
                {uploadingFlyer ? 'Uploading...' : 'Select Image'}
              </Button>
              <p className="text-xs text-muted-foreground mt-3">
                JPG, PNG, GIF or WEBP. Max 5MB.
              </p>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
